/**
 * Common functions and utils service file
 *
 * These functions are general one that doesn't need auth token
 *
 * @package    src/services
 * @author     WangTuring <wangwang@turing.com>
 * @copyright  2018 Turing Company
 * @license    Turing License
 * @version    2.0
 * @link       https://turing.ly
 */
import { checkAdminValidation } from './Auth'
import { ApiConstant, CodeConstant } from '../constants';
import axios from 'axios';
import getEnv from '../../services/util/getEnv';

/**
 * Redirect to another url
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param
 * @return  object
 */
export function redirectPage(pageName) {
  let prefix = window.location.host.startsWith('http') ? '' : 'http://'
  const prependSlash = pageName && !pageName.startsWith('/');
  let url = `${prefix}${window.location.host}${prependSlash ? '/' : ''}${pageName}`;
  window.location.replace(url)
}

/**
 * Redirect to another url
 *
 * @author  DongTuring <dong@turing.com>
 * @param
 * @return  object
 */
export function goToAdminLoginPage() {
  localStorage.setItem('adminAuthenticated', false)
  let prefix = window.location.host.startsWith('http') ? '' : 'http://'
  let url = prefix + window.location.host + '/admin/login'
  window.location.replace(url)
}

/**
 * Check admin token
 *
 * @author  DongTuring <dong@turing.com>
 * @param
 * @return  object
 */
export function checkAdminTokenValidation() {
  let promiseObj = checkAdminValidation()

  promiseObj.then(res => {
    if (res.data.code === CodeConstant.OK) {
      console.log('success')
    } else {
      goToAdminLoginPage()
    }
  }).catch(error => {
    goToAdminLoginPage()
  })
}

/**
 * Get year list til this year.
 */
export function getYYList() {
  let yearsList = [];
  let yearNow = new Date().getFullYear();
  yearsList.push(yearNow);

  for (let i = 0; i < 50; i ++) {
    yearNow = yearNow - 1;
    yearsList.push(yearNow);
  }

  return yearsList;
}

/**
 * Get year of experience list
 */
export function getYOEList() {
  let yearsList = [];
  for (let i = 1; i <= 50; i ++) {
    yearsList.push(i);
  }

  return yearsList;
}

/**
 * Get label for stack value
 */
export function getStackLabel(stackList, value) {
  for (let i = 0; i <= stackList.length; i ++) {
    if (stackList[i].value === value) {
      return stackList[i].label;
    }
  }

  return '';
}

/**
 * Convert from HH:MM:SS 24-hours to HH:MM AM/PM
 */
export function convertHMAPM(time) {
  if (!time || time === '') return '';

  let hms = time.split(':');
  let hh = parseInt(hms[0]);
  let apm = 'AM';

  if (hh >= 12) {
    apm = 'PM';
    if (hh > 12) hh -= 12;
  }

  let hmapm = `${hh}:${hms[1]} ${apm}`;
  if (hh < 10) hmapm = `0${hh}:${hms[1]} ${apm}`;

  return hmapm;
}

/**
 * Convert from HH:MM AM/PM to HH:MM:SS 24-hours
 */
export function convertHMS24(time) {
  if (!time || time === '') return;

  let base = time.split(':');
  let hh = base[0];
  let mm = base[1].split(' ')[0];
  let apm = base[1].split(' ')[1];

  if (parseInt(hh) !== 12 && apm === 'PM') {
    hh = parseInt(hh) + 12;
  }

  let hms24 = `${hh}:${mm}:00`;

  return hms24
}

/*
* Get the URL of Google Cloud Storage
*/
export function getGCSUrl() {
  let url = getEnv("REACT_APP_ENV") === 'production'
    ? getEnv("REACT_APP_PROD_GCS_BUCKET_URL")
    : getEnv("REACT_APP_DEV_GCS_BUCKET_URL");

  return url;
}

/*
 * Returns a promise that resolves after the time set
 */
export const resolveAfter = (milliSeconds, returnValue = '') =>
  new Promise(resolve => setTimeout(resolve, milliSeconds, returnValue))

export const isValidUrl = url =>
  /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/.test(url)

/**
 * Returns experience level list
 * any number > 4 is also 'More than 4 years'
 */
export const experienceLevelList = [
  'No experience',
  'Less than a year',
  '1 to 2 years',
  '3 to 4 years',
  'More than 4 years'
];

export const englishProficiencyList = [
  'Basic', 'Average', 'Great'
]

export const salaryTypeMap = {
  3: 'expected_salary_amount_yearly'
}

export const salaryTypeToTimeMap = {
  HOURLY: 1,
  MONTHLY: 2,
  ANNUALY: 3
}

export const englishProficiencyMapping = {
  great: 'Both U.S. engineering managers and I can easily understand each other without having to repeat much.',
  average: "I can communicate with a U.S. engineering manager if we both speak slowly and occasionally repeat sentences that the other person didn't understand.",
  basic: 'I can only communicate in written form over email or Slack effectively. Verbal communication in English with a U.S. engineering manager would be slow and impractical.'
}

export const preferredRolesToDisplay = [
  'Web Frontend',
  'Web Backend',
  'Fullstack (FE-heavy)',
  'Fullstack (BE-heavy)',
  'Mobile',
  'Machine Learning',
  'DevOps',
  'Cloud - Security',
  'Cloud - Networking',
  'Cloud - Solutions Architecture',
  'Cloud - Data',
  'Cloud - AI/ML',
  'Data Scientist/Data analyst',
  'Other'
]

export const cloudCertificatesRoles = [
  'DevOps',
  'Cloud - Security',
  'Cloud - Networking',
  'Cloud - Solutions Architecture',
  'Cloud - Data',
  'Cloud - AI/ML',
]

export function checkReferralInfos () {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve({});
  const header = {
    'Content-Type': 'application/json',
    'Authorization': token
  };

  return axios
    .post(`${ApiConstant.BASE_URL}common-user/`, { action: 'CHECK_REFERRAL_USER' }, { headers: header })
    .then(response => response.data)
    .then(data => {
      if (data && data.success && data.isReferral && data.totalInvited < 3) {
        return { showLanding: true, ...data };
      } else {
        localStorage.removeItem('ref');
        localStorage.removeItem('tempId');
        localStorage.removeItem('ref_email');
        return { showLanding: false, ...data };
      }
    })
    .catch(console.error);
};


export const getUserEntranceInformation = (authType) => {
   // Get relevant data from the browser
   const entryUrl = window.location.href;
   const auth = authType;
   const referrerUrl = document.referrer;
 
   return { entryUrl, auth, referrerUrl, };
}